import React from "react";
import { Link } from "react-router-dom";
import useReveal from "../../hooks/useReveal";
import usePricingData from "../../hooks/usePricingData";
import { formatPriceMXN } from "../../lib/pricingFormat";
import { waUrl } from "../../config/bdk";

const Pricing = () => {
  useReveal();
  const { data } = usePricingData();
  const servicesByCode = Object.fromEntries(
    data.categories.flatMap((c) => c.items).map((item) => [item.code, item])
  );
  const highlights = data.landingHighlights.map((h) => ({
    ...h,
    id: h.service_code,
    price: formatPriceMXN(servicesByCode[h.service_code]?.price),
  }));
  return (
    <section
      id="tipos-paginas"
      data-testid="section-paquetes"
      className="bdk-section relative overflow-hidden isolate"
      style={{ background: "var(--ink)", color: "#fff" }}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 80% 20%, rgba(217,0,32,.18), transparent 45%)",
        }}
      />

      <div className="container-bdk relative">
        <div className="max-w-[760px] mx-auto text-center mb-16 bdk-reveal bdk-reveal--up">
          <p className="kicker">Tipos de páginas web</p>
          <h2 className="font-display text-[38px] sm:text-[48px] md:text-[56px] lg:text-[64px]">
            Elige el tipo de página
            <br />
            que necesita tu negocio.
          </h2>
          <p className="mt-5 text-[#b9bac3] text-[15px] md:text-[16px]">
            Precios de partida claros. El alcance final se revisa contigo
            antes de cotizar.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-5 items-stretch">
          {highlights.map((p, i) => {
            const featured = p.featured;
            const cardStyle = featured
              ? {
                  border: "2px solid var(--red)",
                  background: "var(--ink-2)",
                  boxShadow: "0 22px 55px rgba(217,0,32,.25)",
                }
              : { border: "1px solid #2a2b34", background: "var(--ink-2)" };

            return (
              <article
                key={p.id}
                data-testid={`pricing-card-${p.id}`}
                className={`relative flex flex-col p-8 rounded-[14px] bdk-reveal bdk-reveal--up ${
                  featured ? "xl:-translate-y-3 featured-pulse" : ""
                }`}
                style={{
                  ...cardStyle,
                  "--reveal-delay": `${i * 90}ms`,
                }}
              >
                <div
                  className="absolute -top-[15px] left-1/2 -translate-x-1/2 px-3.5 py-1.5 rounded-full whitespace-nowrap"
                  style={{
                    background: "var(--red)",
                    color: "#fff",
                    fontSize: "10px",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: ".5px",
                  }}
                >
                  {p.badge}
                </div>

                <p className="font-display text-[24px] font-black uppercase mt-2 leading-tight">
                  {p.name}
                </p>

                <div
                  className="font-display font-black leading-none my-4"
                  style={{ color: "var(--red)", fontSize: "40px" }}
                >
                  <span className="text-[13px] font-bold uppercase tracking-wide align-top mr-1 text-[#b9bac3]">
                    Desde
                  </span>
                  <br />
                  <span className="text-[22px] align-top mr-1">$</span>
                  {p.price}
                  <small className="text-[12px] ml-1 font-black">MXN</small>
                </div>

                <p className="text-[13px] min-h-[80px] text-[#b8b9c1]">
                  {p.ideal}
                </p>

                <p
                  className="text-[12px] mt-2 mb-5"
                  style={{ color: "#999aa2" }}
                >
                  {p.delivery_label}
                </p>

                <a
                  href={waUrl(p.cta_msg)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`pricing-cta-${p.id}`}
                  onClick={() =>
                    window.fbq?.("trackCustom", "ClickSolucion", {
                      solucion: p.id,
                    })
                  }
                  className={`mt-auto bdk-btn ${
                    p.variant === "primary"
                      ? "bdk-btn--primary"
                      : "bdk-btn--ghost-dark"
                  }`}
                >
                  {p.cta_text}
                </a>
              </article>
            );
          })}
        </div>

        {/* Secondary category: custom web development, deliberately lower visual weight */}
        <div
          className="mt-6 rounded-[14px] p-7 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 bdk-reveal bdk-reveal--up"
          style={{
            background: "var(--ink-2)",
            border: "1px dashed rgba(255,255,255,.15)",
          }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-widest font-black text-[#b8b9c1]">
              Desarrollo web a la medida
            </p>
            <h3 className="font-display text-[22px] md:text-[24px] leading-tight mt-1">
              ¿Tu negocio necesita algo que no cabe en una página convencional?
            </h3>
            <p className="text-[#b8b9c1] text-[13px] mt-2 max-w-[560px]">
              También desarrollamos sistemas web, portales de clientes,
              dashboards, áreas privadas, integraciones, automatizaciones y
              herramientas web adaptadas a procesos específicos. Requiere
              análisis y cotización personalizada.
            </p>
          </div>
          <a
            href={waUrl(
              "Hola, tengo un proyecto de desarrollo web a la medida (sistema, portal o herramienta específica). Quiero cotizarlo con BDKSISTEMAS."
            )}
            target="_blank"
            rel="noreferrer"
            data-testid="pricing-cta-medida"
            className="bdk-btn bdk-btn--ghost-dark flex-shrink-0"
          >
            Consultar proyecto especial
          </a>
        </div>

        <div className="mt-8 text-center max-w-2xl mx-auto">
          <p className="text-[12px] text-[#b8b9c1]">
            Todos los precios están en pesos mexicanos, más IVA, y pueden
            cambiar sin previo aviso. El precio final depende del alcance y
            las funciones requeridas: revisamos tu proyecto antes de emitir
            una cotización formal. Dominio y hosting se cotizan por separado
            salvo que una propuesta indique lo contrario.
          </p>
          <a
            href="#contacto"
            data-testid="pricing-cta-orientacion"
            className="bdk-btn bdk-btn--ghost-dark mt-5"
          >
            No sé cuál necesito
          </a>
          <p className="text-[12px] text-[#999aa2] mt-6">
            <Link
              to="/tipos-de-paginas-web"
              data-testid="pricing-link-lista-completa"
              onClick={() => window.fbq?.("trackCustom", "ClickListaCompleta")}
              className="underline font-semibold"
              style={{ color: "var(--red)" }}
            >
              Ver los 17 tipos de página y precios completos
            </Link>
          </p>
          <p className="text-[15px] md:text-[16px] text-[#b8b9c1] mt-5">
            ¿Buscas imágenes publicitarias o video con IA para promocionar tu
            página?
            <br />
            <Link
              to="/otros-servicios"
              data-testid="pricing-link-otros-servicios"
              onClick={() => window.fbq?.("trackCustom", "ClickOtrosServicios")}
              className="underline font-semibold"
              style={{ color: "var(--red)" }}
            >
              Conoce otros servicios creativos
            </Link>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
